import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { ContractTypeEnum, ReleaseTypeEnum } from '@soundxyz/utils/const';
import { Button } from '@components/main-components/button/Button';
import { quantityReducer } from '@components/main-components/inputs/QuantityInput';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import {
  type FragmentType,
  getFragment,
  gql,
  MarketplaceCheckoutFragmentDoc,
} from '@graphql/reactQuery';
import { useConnectedWallet } from '@hooks/useConnectedWallet';
import type { MarketPlaceCollectionType } from '@hooks/useMarketPlace';
import { useMarketPlace } from '@hooks/useMarketPlace';
import { ReleaseDetails } from '../shared';
import { PriceBreakdown } from './PriceBreakdown';
import { SecondaryPanel } from './SecondaryPanel';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment MarketplaceCheckout on Release {
    id
    chainId
    title
    type
    contract {
      id
      contractType
    }
    ...ReleaseDetails
  }
`);

export function getCollectionTypeForMarketplace({
  contractType,
  releaseType,
}: {
  contractType: ContractTypeEnum;
  releaseType: ReleaseTypeEnum;
}): MarketPlaceCollectionType {
  return contractType === 'ARTIST'
    ? 'Artist Contract'
    : contractType === 'TIERED_EDITION'
    ? 'Tiered Edition'
    : releaseType === 'ALBUM_TRACK'
    ? 'Album Edition'
    : 'V1 Edition';
}

export const MarketPlaceCheckoutModal = ({
  releaseData,
  contractAddress,
  marketPlacePlaceUrl,
}: {
  releaseData: FragmentType<MarketplaceCheckoutFragmentDoc>;
  contractAddress: string;
  marketPlacePlaceUrl: string | null;
}) => {
  const release = getFragment(MarketplaceCheckoutFragmentDoc, releaseData);
  const {
    id: releaseId,
    chainId,
    title: releaseTitle,
    type: releaseType,
    contract: { contractType },
  } = release;

  const [state, dispatch] = React.useReducer(quantityReducer, {
    value: 1,
    error: false,
  });

  const collectionType = getCollectionTypeForMarketplace({
    contractType,
    releaseType,
  });

  const {
    marketPlaceFloorPriceInWei,
    marketPlaceTokens,
    collectMarketPlace,
    isCollecting,
    isSwitchingNetwork,
    state: buttonState,
    isSufficientFunds,
    totalCost,
    isLoading,
  } = useMarketPlace({
    releaseId,
    contractAddress: contractAddress,
    contractChainId: chainId,
    quantity: state.value || 0,
    releaseTitle,
    collectionType,
  });
  const { closeCancelModal } = ModalContext.useContainer();

  const isBusy = isCollecting || isSwitchingNetwork || isLoading;
  const disabled = !marketPlaceTokens.length || !isSufficientFunds;

  const checkout = (
    <div className="flex flex-col gap-4">
      <ReleaseDetails releaseData={release} selectedMintPanel="LIMITED" />
      <div className="flex flex-col gap-4">
        <SecondaryPanel
          state={state}
          dispatch={dispatch}
          marketFloorPrice={marketPlaceFloorPriceInWei}
          tokens={marketPlaceTokens}
          disabled={isBusy}
        />
        <PriceBreakdown totalCost={totalCost} chainId={chainId} />
      </div>
    </div>
  );

  const handleBuy = useConnectedWallet({
    onAuth: () => collectMarketPlace({ quantity: state.value || 0 }),
  });

  const ctaButtons = (
    <div className="flex w-full flex-col items-center gap-3">
      <Button
        label={buttonState}
        variant={isBusy || !isSufficientFunds ? 'primary' : 'fuchsia'}
        onClick={handleBuy}
        fullWidth
        loading={isBusy}
        disabled={disabled}
      />
      {!!marketPlacePlaceUrl && (
        <Button
          label="View on Sound Market"
          variant="text"
          href={marketPlacePlaceUrl}
          className="h-4 p-0 px-2 text-base-s text-base500 transition-colors duration-300 hover:text-base800"
          size="XS"
          trailingIcon={<FontAwesomeIcon icon={faArrowUpRight} />}
        />
      )}
    </div>
  );

  return (
    <StandardModal
      title="Checkout"
      body={checkout}
      closeModal={closeCancelModal}
      callToAction={ctaButtons}
      bodyWithoutPadding
    />
  );
};
